import { useState } from "react";
import { Check, X, RotateCcw, BookMarked } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import Crossword from "@/components/assets/Crossword";

const RTL = new Set(["ar", "he", "fa", "ur"]);

function Flashcards({ cards, onDone }) {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(false);
  const [res, setRes] = useState({});
  if (i >= cards.length) return null;
  const c = cards[i];
  const mark = (known) => { const r = { ...res, [c.id]: known }; setRes(r); setFlip(false); setI(i + 1); if (i + 1 >= cards.length) onDone?.(r); };
  return (
    <div className="space-y-3" data-testid="asset-flashcards">
      <div className="text-xs text-gray-500" dir="ltr" data-testid="asset-card-index">{t("al.cardOf", { i: i + 1, n: cards.length })}</div>
      <button onClick={() => setFlip(!flip)} className="nb-card w-full min-h-[8rem] p-6 text-2xl font-bold bg-butter" data-testid="asset-card">
        <bdi>{flip ? c.back : c.front}</bdi>
        {!flip && <div className="text-xs font-normal text-gray-500 mt-2">{t("al.flip")}</div>}
      </button>
      <div className="flex gap-2">
        <button onClick={() => mark(true)} className="nb-btn-primary py-2 px-4 flex-1" data-testid="asset-card-know"><Check className="w-4 h-4" /> {t("al.know")}</button>
        <button onClick={() => mark(false)} className="nb-btn-outline py-2 px-4 flex-1" data-testid="asset-card-again"><RotateCcw className="w-4 h-4" /> {t("al.again")}</button>
      </div>
    </div>
  );
}

function Items({ items, onCheck, answers }) {
  const { t } = useI18n();
  const [val, setVal] = useState({});
  const [res, setRes] = useState(null);
  const check = async () => setRes(await onCheck(val));
  return (
    <div className="space-y-3" data-testid="asset-items">
      {items.map((it) => (
        <div key={it.id} className="space-y-1">
          <div className="text-sm font-medium"><span dir="auto">{it.prompt}</span> {res && (res.results[it.id] ? <Check className="w-4 h-4 inline text-green-700" /> : it.id in res.results && <X className="w-4 h-4 inline text-coral" />)}</div>
          {it.options ? (
            <div className="flex flex-wrap gap-1.5" dir={/[\u0600-\u06FF]/.test(it.options.join("")) ? "rtl" : "ltr"}>
              {it.options.map((o) => <button key={o} onClick={() => setVal({ ...val, [it.id]: o })} className={`nb-tag text-sm normal-case tracking-normal ${val[it.id] === o ? "bg-sky" : "bg-white"}`} data-testid={`asset-opt-${it.id}-${it.options.indexOf(o)}`} dir="auto">{o}</button>)}
            </div>
          ) : <input className="nb-input" dir="auto" maxLength={200} value={val[it.id] || ""} onChange={(e) => setVal({ ...val, [it.id]: e.target.value })} disabled={!onCheck} data-testid={`asset-input-${it.id}`} />}
          {answers?.[it.id] && <div className="text-xs text-gray-600" data-testid={`asset-key-${it.id}`}>✓ <bdi>{answers[it.id].join(" / ")}</bdi></div>}
        </div>
      ))}
      {onCheck && (
        <div className="flex items-center gap-3">
          <button onClick={check} className="nb-btn-primary py-2 px-4" data-testid="asset-check">{t("al.check")}</button>
          {res && <span className="text-sm font-bold" data-testid="asset-score">{t("al.score", { c: res.correct, t: res.total })}</span>}
          {res && <button onClick={() => { setRes(null); setVal({}); }} className="text-sm underline" data-testid="asset-retry">{t("al.tryAgain")}</button>}
        </div>
      )}
    </div>
  );
}

// One renderer for Discovery Lab, class assignments and teacher preview. Answers arrive only in teacher view.
export default function AssetPlayer({ family, language, content, onCheck, onPractice, onFlashDone, keep, onKeep, answers, initialDone = false }) {
  const { t } = useI18n();
  const [practised, setPractised] = useState(initialDone);
  const [kept, setKept] = useState(!!keep?.kept);
  const c = content || {};
  const dir = RTL.has(language) ? "rtl" : "ltr";
  const flash = family === "memory" && c.cards?.length;
  return (
    <div className="space-y-4" data-testid="asset-player">
      <div lang={language} className="space-y-3">
        {c.intro && <p className="text-sm" dir="auto">{c.intro}</p>}
        {c.text && <p className="nb-card bg-white p-4 leading-relaxed" dir="auto" data-testid="asset-text">{c.text}</p>}
        {c.table && (
          <div className="overflow-x-auto" dir={dir}><table className="w-full text-sm border-2 border-ink" data-testid="asset-table">
            <thead><tr>{c.table.columns.map((h) => <th key={h} className="border border-ink/30 p-2 bg-lavender text-start">{h}</th>)}</tr></thead>
            <tbody>{c.table.rows.map((r, i) => <tr key={i}>{r.map((x, j) => <td key={j} className="border border-ink/30 p-2">{x}</td>)}</tr>)}</tbody>
          </table></div>
        )}
        {c.sections?.map((s) => <div key={s.heading} dir="auto"><div className="font-bold">{s.heading}</div><p className="text-sm">{s.body}</p></div>)}
        {!flash && c.cards?.map((k) => <div key={k.id} className="nb-card bg-white p-3" dir="auto" data-testid={`asset-role-${k.id}`}><div className="font-bold">{k.title || k.front}</div><p className="text-sm">{k.body || k.back}</p></div>)}
      </div>
      {flash && !practised && <div><Flashcards cards={c.cards} onDone={(r) => { setPractised(true); onPractice?.(r); onFlashDone?.(); }} /></div>}
      {flash && practised && (
        <div className="flex items-center gap-3"><p className="font-bold flex items-center gap-2" data-testid="asset-practice-done"><Check className="w-4 h-4" /> {t("al.done")}</p>
          <button onClick={() => setPractised(false)} className="text-sm underline" data-testid="asset-practise-again">{t("al.p.again")}</button></div>
      )}
      {c.crossword && <Crossword cw={c.crossword} language={language} onCheck={onCheck} answers={answers} />}
      {c.items && <div><Items items={c.items} onCheck={onCheck} answers={answers} /></div>}
      {flash && keep && (practised || !onPractice) && (
        keep.can_keep ? (
          kept ? <p className="text-sm font-bold" data-testid="asset-kept">{t("al.kept")}</p>
            : <button onClick={async () => setKept(await onKeep())} className="nb-btn-outline py-2 px-4 text-sm" data-testid="asset-keep"><BookMarked className="w-4 h-4" /> {t("al.keep")}</button>
        ) : <p className="text-xs text-gray-600" data-testid="asset-cant-keep">{t("al.cantKeep")}</p>
      )}
    </div>
  );
}
